import Big from 'big.js';
import dateFns from 'date-fns';
import {FOUR_WEEKS_UNIX_TIME, TEXT_TO_JOIN_DESCRIBE} from './constants';
import moment from "moment";

const sampleReplacements = {
  sample1: "sampleOne",
  sample2: "sampleTwo",
  sample3: "sampleThree",
  sample4: "sampleFour",
  sample5: "sampleFive",
};

const getDefaultOptInMessage = (companyName) => {
  return `You’ve subscribed to receive recurring messages from ${companyName}. Reply STOP to cancel, reply HELP for help. Message frequency varies. Message and Data rates may apply. Carriers are not liable for delayed or undelivered messages.`
}

const BUSINESS_MESSAGES = {
  INSURANCE_AGENCY: 'Business type: Insurance provider. Acknowledged that SMS usage is strictly limited to insurance-related client support, with no unsolicited quotes or marketing messages.',
  RECRUITING_AGENCY: 'Business type: Recruiting. Acknowledged that SMS usage is strictly limited to sending relevant job opportunities to candidates, with no unsolicited marketing or unrelated job postings.',
  FINANCIAL_AGENCY: 'Business type: Financial services. Acknowledged that SMS usage is strictly limited to client services/support, with no unsolicited marketing or promotional messages.'
};

const getDescriptionMessage = (companyName, businessType) => {
  const baseMessage = `This is a campaign registered by Vonage on behalf of their VBC UC app customer ${companyName} in order to allow this customer to have conversational traffic with their employees and or customers through the approved Vonage Business Cloud (VBC) application.`;
  return BUSINESS_MESSAGES[businessType] ? `${baseMessage} ${BUSINESS_MESSAGES[businessType]}` : baseMessage;
};

const getBulkSmsDescriptionMessage = (companyName) => {
  return `This is a campaign registered by Vonage on behalf of their VBC UC app customer ${companyName}. ${companyName} uses SMS to communicate with its customers and/or staff for business-related purposes in accordance with selected sub use cases. Messages may be sent in bulk on a low-volume basis and recipients have opted in to receive communications from ${companyName}.`;
};

const copyToClipboard = text => {
  const tempInput = document.createElement('textarea');
  document.querySelector('body').append(tempInput);
  tempInput.value = text;
  tempInput.select();
  document.execCommand('copy');
  tempInput.remove();
};

const getErrorMessage = (error, preferableDataProp) => {
  const { message, response, request } = error;
  const SANITISER_DETAIL_ERROR = 'Mapping error'; // for sanitiser we should return the title not the detail

  if (response) {
    const { data } = response;
    if (data) {
      if (preferableDataProp && data[preferableDataProp]) return data[preferableDataProp];
      if (
          data.invalid_parameters &&
          Array.isArray(data.invalid_parameters) &&
          data.invalid_parameters.length === 1 &&
          data.invalid_parameters[0].name === ''
      ) {
        return data.invalid_parameters[0].reason;
      }
      if (data.invalid_parameters && Array.isArray(data.invalid_parameters)) {
        return data.invalid_parameters.reduce((acc, curr) => ({ ...acc, [curr.field]: curr.description }), {});
      }
      if (data.errors && data.errors.length > 0) return data.errors[0];
      if (data.detail && data.detail !== SANITISER_DETAIL_ERROR) return data.detail;
      if (data.title) return data.title;
      if (data.length > 0 && Array.isArray(data)) return data[0];
      if (data.errorMessage) return data.errorMessage;
      return data;
    }
  }

  if (message) return message;

  if (request && request.response) {
    return JSON.parse(request.response).title;
  }

  return error;
};

const changeObjectKeys = (obj = {}, fcn = key => key) =>
    Object.keys(obj).reduce((acc, currentKey) => ({ ...acc, [fcn(currentKey)]: obj[currentKey] }), {});

const toCamelCase = text => text.replace(/(_\w)/g, m => m[1].toUpperCase());

const toSnakeCase = text => text.replace(/([A-Z])/g, '_$1').toLowerCase();

const changeObjectKeysToCamelCase = obj => changeObjectKeys(obj, toCamelCase);

const changeObjectKeysToSnakeCase = obj => changeObjectKeys(obj, toSnakeCase);

const changeToCurrencyNumber = (number, paddingSize = 2) => {
  const bigNumber = new Big(number);
  return bigNumber.toFixed(paddingSize).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,');
};

const arrayToMap = (arr, prop) => arr.reduce((acc, val) => ({ ...acc, [val[prop]]: val }), {});

const changeDateFormat = (date, format = 'DD-MMM-YYYY') => dateFns.format(date, format);

const getTimeLeft = (endDate, startDate = new Date()) => {
  const differenceInHours = dateFns.differenceInHours(endDate, startDate);
  if (differenceInHours < 24) {
    if (differenceInHours === 0) {
      return 'less than 1 hour';
    }
    return `${differenceInHours} hour${differenceInHours === 1 ? '' : 's'}`;
  }
  const differenceInDays = dateFns.differenceInDays(endDate, startDate);
  return `${differenceInDays} day${differenceInDays === 1 ? '' : 's'}`;
};

const matchParams = (newSearchParams, lastSearchParams) =>
  Object.keys(newSearchParams).reduce(
    (acc, currentKey) =>
      acc &&
      (currentKey === 'searchPattern' && lastSearchParams[currentKey] !== newSearchParams[currentKey]
        ? !newSearchParams.number
        : lastSearchParams[currentKey] === newSearchParams[currentKey]),
    true
  );


const createSearchNumbersParams = (
  apiKey = '',
  searchPattern = '',
  features = '',
  type = '',
  country = '',
  number = ''
) => ({
  country,
  features,
  number,
  type,
  searchPattern,
  apiKey,
});

const sortArray = (array, property, isAsc = true) => {
  const sorted = [...array].sort((a, b) => {
    let aVal = a[property];
    let bVal = b[property];

    if (!aVal && aVal !== 0) return 1;
    if (!bVal && bVal !== 0) return -1;
    if (aVal === bVal) return 0;

    aVal = typeof aVal === 'string' ? aVal.toUpperCase() : aVal;

    bVal = typeof bVal === 'string' ? bVal.toUpperCase() : bVal;

    if (isAsc) {
      return aVal > bVal ? 1 : -1;
    }
    return aVal > bVal ? -1 : 1;
  });

  return sorted;
};

const prepareMessageFlowDetailsBulkSms = (displayName, messagingDescription) => {
  const consentMechanisms = [];
  Object.keys(messagingDescription).forEach(key => {
    if (key === "onlineCheckBox") {
      if (messagingDescription[key]?.describe?.trim() || messagingDescription[key]?.url?.trim()) {
        consentMechanisms.push({ method: 'ONLINE_FORM', details: messagingDescription[key].describe, attachment: messagingDescription[key].url });
      }
    } else if (key === "verbalCheckBox") {
      if (messagingDescription[key]?.describe?.trim() || messagingDescription[key]?.url?.trim()) {
        consentMechanisms.push({ method: 'LIVE_OPERATOR', details: messagingDescription[key].describe, attachment: messagingDescription[key].url });
      }
    } else if (key === "paperCheckBox") {
      if (messagingDescription[key]?.describe?.trim() || messagingDescription[key]?.url?.trim()) {
        consentMechanisms.push({ method: 'POINT_OF_SALE', details: messagingDescription[key].describe, attachment: messagingDescription[key].url });
      }
    } else if (key === "impliedCheckBox") {
      if (messagingDescription[key]?.describe?.trim() || messagingDescription[key]?.url?.trim()) {
        consentMechanisms.push({ method: 'OTHER', details: messagingDescription[key].describe, attachment: messagingDescription[key].url });
      }
    }
  });
  consentMechanisms.push({
    method: 'TEXT_TO_JOIN',
    details: TEXT_TO_JOIN_DESCRIBE,
  });

  return {
    brand_name: displayName,
    consent_mechanisms: consentMechanisms,
    pricing_disclosure: true,
    carrier_disclaimer: true,
    frequency: "RECURRING",
    privacy_policy: (messagingDescription?.hasPrivacyPolicy && messagingDescription?.linkToPrivacyPolicy)
      ? messagingDescription.linkToPrivacyPolicy
      : "https://www.notapplicable.com",
    terms_and_conditions: "https://www.notapplicable.com"
  };
};

const prepareMessageFlowDetails = (displayName, messagingDescription) => {
  const consentMechanisms = [];
  Object.keys(messagingDescription).forEach(key => {
    if (key === "onlineCheckBox") {
      consentMechanisms.push({
        method: 'ONLINE_FORM',
        details: messagingDescription[key].describe,
        attachment: messagingDescription[key].url
      });
    } else if (key === "verbalCheckBox") {
      consentMechanisms.push({
        method: 'LIVE_OPERATOR',
        details: messagingDescription[key].describe,
        attachment: messagingDescription[key].url
      });
    } else if (key === "paperCheckBox") {
      consentMechanisms.push({
        method: 'POINT_OF_SALE',
        details: messagingDescription[key].describe,
        attachment: messagingDescription[key].url
      });
    } else if (key === "impliedCheckBox") {
      consentMechanisms.push({
        method: 'OTHER',
        details: messagingDescription[key].describe,
        attachment: messagingDescription[key].url
      });
    }
  });

  consentMechanisms.push({
    method: 'TEXT_TO_JOIN',
    details: TEXT_TO_JOIN_DESCRIBE,
  })

    return {
        brand_name : displayName,
        consent_mechanisms : consentMechanisms,
        pricing_disclosure :  true,
        carrier_disclaimer : true,
        frequency : "RECURRING",
        privacy_policy : (messagingDescription?.hasPrivacyPolicy && messagingDescription?.linkToPrivacyPolicy)
        ? messagingDescription.linkToPrivacyPolicy
        : "https://www.notapplicable.com",
        terms_and_conditions : "https://www.notapplicable.com"
    }



}

const convertMessagingDescriptionArrayToObj = (messagingDescriptionPageFiltered) => {
  return messagingDescriptionPageFiltered.reduce((result, [key, value]) => {
    if (typeof value === 'object') {
      //extract field we dont want to send to the backend
      // eslint-disable-next-line no-unused-vars
      const {isCheck, header,optInConfirmationMessageEditEnable, ...filterCheckBoxData} = value;
      result[key] = filterCheckBoxData;
    } else {
      result[key] = value;
    }
    return result;
  }, {});
}

const convertRegistrationResponseToMessageContentPage = (registrationResponse) =>  {
  const {campaignSamples,  ...messageContentDetailsResponse} = {...registrationResponse.message_content_page}
  const campaignSamplesFilter = Object.entries(campaignSamples || {}).reduce((acc,[key,value]) => {
    if (value !== null) {
       acc[key] = value
    }
    return acc
  }, {} )
  let messageContentDetails = Object.entries(messageContentDetailsResponse).reduce((acc,[key,value]) => {
    if (value === null ) {
      acc[key] = ""
    } else {
      acc[key] = value
    }
    return acc
  }, {})
  messageContentDetails.helpMessageEditable = true
  messageContentDetails.stopMessageEditable = true
  messageContentDetails.campaignSamples = campaignSamplesFilter
  if(!messageContentDetails.campaignSamples.sample2) {
    messageContentDetails.campaignSamples.sample2 = ""
  }
  return messageContentDetails
}

const convertRegistrationResponseToMessagingDescriptionPage = (registrationResponse) =>  {
  const {linkToPrivacyPolicy, linkToTermsAndConditions, typicalMessage, hasPrivacyPolicy, businessType, ...checkBoxes} = {...registrationResponse.messaging_description_page}
  let messagingDescription = Object.entries(checkBoxes).reduce((acc,[key,value]) => {
    if (key === "onlineCheckBox" || key === "verbalCheckBoxV2" || key === "paperCheckBoxV2" || key === "impliedCheckBox") {
      let objNewRequirements = {}
      switch (key){
        case "onlineCheckBox":
          objNewRequirements.header = "Online (Site, Mobile app/browser)"
          break ;
        case "paperCheckBoxV2":
          objNewRequirements.header = "Paper Form"
              break;
        case "verbalCheckBoxV2":
          objNewRequirements.header = "Verbal Interaction"
              break
        case "impliedCheckBox":
          objNewRequirements.header = "Implied consent (recipients message us first)"
              break
        default:
          console.log("error")
      }
          if (value === null) {
            objNewRequirements.isCheck = false
            objNewRequirements.url = ""
            objNewRequirements.describe = ""
          } else {
            objNewRequirements.isCheck = true
            objNewRequirements.url = value.url
            objNewRequirements.describe = value.describe
          }
          acc[key] = objNewRequirements
    } else if (key === "paperCheckBox" || key === "verbalCheckBox") {
      let obj = {}
      obj.header = key === "paperCheckBox" ? "Paper Form" : "Verbal Interaction"
      if (value === null) {
        obj.isCheck = false
        obj.describe = ""
      } else {
        obj.isCheck = true
        obj.describe = value
      }
      obj.url = ""
      acc[key] = obj
    }
    return acc
  },{})
  messagingDescription.linkToPrivacyPolicy = linkToPrivacyPolicy ?? '';
  messagingDescription.linkToTermsAndConditions = linkToTermsAndConditions ?? '';
  messagingDescription.typicalMessage = typicalMessage ?? '';
  if (hasPrivacyPolicy !== undefined && hasPrivacyPolicy !== null) {
    messagingDescription.hasPrivacyPolicy = hasPrivacyPolicy;
  }
  if (businessType !== undefined) {
    messagingDescription.businessType = businessType;
  }
  return messagingDescription;
}

const handleMessagingDescriptionNewRequirementsForUpdatesRequired = (messagingDescription) => {
  if(messagingDescription.paperCheckBoxV2?.isCheck) {
    messagingDescription.paperCheckBox = messagingDescription.paperCheckBoxV2;
  }
  if(messagingDescription.verbalCheckBoxV2?.isCheck) {
    messagingDescription.verbalCheckBox = messagingDescription.verbalCheckBoxV2;
  }
  messagingDescription.verbalCheckBox.verbalEditEnable = false;
  delete messagingDescription.verbalCheckBoxV2;
  delete messagingDescription.paperCheckBoxV2;
  return messagingDescription;
}

const buildCampaignObject = (messagingDescription, messageContent, companyName, displayName, vertical, volumeType, entityType) => {
  let samples = Object.keys(messageContent.campaignSamples).reduce(
      (acc, key) => ({
        ...acc,
        [sampleReplacements[key]]: messageContent.campaignSamples[key],
      }),
      {}
  );
  const messageFlowDetails = prepareMessageFlowDetails(displayName, messagingDescription)
  samples = changeObjectKeysToSnakeCase(samples);
  const campaignUsecase = entityType === 'SOLE_PROPRIETOR' ? 'SOLE_PROPRIETOR' : volumeType;
  const campaignObject = {
    ...samples,
    vertical: vertical,
    label: `${companyName?.substring(0, 40)}_campaign`,
    description: getDescriptionMessage(displayName, messagingDescription.businessType),
    subscriber_opt_in: true,
    subscriber_opt_out: true,
    subscriber_help: true,
    usecase: campaignUsecase,
    sub_usecases: process.env.VUE_APP_CAMPAIGN_SUB_USE_CASE ? [process.env.VUE_APP_CAMPAIGN_SUB_USE_CASE] : null,
    auto_renewal: true,
    message_flow_details: messageFlowDetails,
    opt_in_message: getDefaultOptInMessage(displayName),
    opt_out_message: messageContent.stopMessage,
    help_message: messageContent.helpMessage,
    help_keywords: "HELP",
    opt_out_keywords: "STOP,END,QUIT,CANCEL,UNSUBSCRIBE,REVOKE,OPT OUT",
    opt_in_keywords: "JOIN",
    embedded_link: true,
    embedded_phone: true,
    number_pool: true,
    tc_agreed: true,
    has_privacy_policy: messagingDescription.hasPrivacyPolicy,
  };
  return campaignObject;
}

const buildBulkSmsHelpMessage = (displayName, formData) => {
  let phone = formData.existingPhone || '';
  let email = formData.existingEmail || '';

  const helpMessage = `${displayName} - For help reach us at `;

  if (phone && email) {
    return `${helpMessage}${phone} or ${email}`;
  } else if (phone) {
    return `${helpMessage}${phone}`;
  } else if (email) {
    return `${helpMessage}${email}`;
  }
  return `${helpMessage}`;
};


const buildBulkSmsCampaignObject = (formData, existingUsecase, messagingDescription = null) => {
  const bulkUsecase = existingUsecase === 'UCAAS_HIGH' ? 'MIXED' : 'LOW_VOLUME';
  const sampleKeys = ['sample1', 'sample2', 'sample3', 'sample4', 'sample5'];
  const filledMessages = (formData.sampleMessages || []).filter(m => m && m.trim() !== '');
  let samples = filledMessages.reduce((acc, msg, idx) => ({
    ...acc,
    [sampleReplacements[sampleKeys[idx]]]: msg.trim(),
  }), {});
  samples = changeObjectKeysToSnakeCase(samples);

  return {
    description: getBulkSmsDescriptionMessage(formData.displayName),
    usecase: bulkUsecase,
    sub_usecases: formData.subUseCases,
    ...samples,
    help_message: buildBulkSmsHelpMessage(formData.displayName, formData),
    opt_in_message: getDefaultOptInMessage(formData.displayName),
    opt_out_message: `${formData.displayName} - You have successfully unsubscribed and will no longer receive messages. To resubscribe at any time, reply JOIN.`,
    opt_in_keywords: 'JOIN',
    opt_out_keywords: 'STOP,END,QUIT,CANCEL,UNSUBSCRIBE,REVOKE,OPT OUT',
    help_keywords: 'HELP',
    embedded_link: false,
    embedded_phone: false,
    number_pool: false,
    age_gated: false,
    direct_lending: false,
    affiliate_marketing: false,
    auto_renewal: true,
    message_flow_details: messagingDescription
    ? prepareMessageFlowDetailsBulkSms(formData.displayName, messagingDescription)
    : null,
    subscriber_opt_in: true,
    subscriber_opt_out: true,
    subscriber_help: true,
    tc_agreed: formData.tcrChecked,
    has_privacy_policy: messagingDescription?.hasPrivacyPolicy ?? false,
  };
};


const getUpdatesRequiredLimitDate = (updatesDate) => {
  const updateUnixTime = Date.parse(updatesDate) + FOUR_WEEKS_UNIX_TIME;
  return moment(updateUnixTime).format("MMM Do YYYY")
}

const allowedHighVolumeCreation = () => {
  const usersString = process.env.VUE_APP_STRONG_USERS
  return usersString.split(',');
}

const advancedStatusPageUsers = () => {
  const usersString = process.env.VUE_APP_ADVANCED_STATUS_PAGE_USERS
  return usersString.split(',');
}

const findIfUserCanSeeNpList=()=>{
  const userList=process.env.VUE_APP_NP_LIST;
  return userList;
}

const getFileUploadUsers = () => {
  const usersString = process.env.VUE_APP_FILE_UPLOAD_USERS;
  return usersString ? usersString.split(',') : [];
}

const isSPAccountsAccessible=()=>{
  const isSpAccounts=process.env.VUE_APP_SP_API_FLAG;
  return isSpAccounts=='true';
}

const supportMessagingDescriptionV2 = (messagingDescription) => {
  messagingDescription["verbalCheckBoxV2"] = messagingDescription.verbalCheckBox
  messagingDescription["paperCheckBoxV2"] = messagingDescription.paperCheckBox
  delete messagingDescription.verbalCheckBox
  delete messagingDescription.paperCheckBox
}

const  capitalizeFirstLetter = (str) => {
  let formattedString =   str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  formattedString = formattedString.replace(/[^a-zA-Z]/g, ' ');
  return formattedString
}

export {
  copyToClipboard,
  changeObjectKeysToCamelCase,
  changeObjectKeysToSnakeCase,
  getErrorMessage,
  toCamelCase,
  toSnakeCase,
  changeToCurrencyNumber,
  arrayToMap,
  changeDateFormat,
  getTimeLeft,
  createSearchNumbersParams,
  matchParams,
  sortArray,
  convertMessagingDescriptionArrayToObj,
  convertRegistrationResponseToMessageContentPage,
  convertRegistrationResponseToMessagingDescriptionPage,
  buildCampaignObject,
  getDefaultOptInMessage,
  getUpdatesRequiredLimitDate,
  allowedHighVolumeCreation,
  advancedStatusPageUsers,
  prepareMessageFlowDetails,
  supportMessagingDescriptionV2,
  handleMessagingDescriptionNewRequirementsForUpdatesRequired,
  capitalizeFirstLetter,
  getDescriptionMessage,
  getBulkSmsDescriptionMessage,
  findIfUserCanSeeNpList,
  buildBulkSmsCampaignObject,
  getFileUploadUsers,
  isSPAccountsAccessible
};
